import { useMemo } from "react";

import { API_URLS } from "shared/constants";
import useHttpActions from "shared/services/api/core/useHttpActions";

import BaseConverter from "./converters/baseConverter";

import type { BaseApiResponse } from "../types/BaseApiResponse";
import { baseApiResponse } from "../types/BaseApiResponse";
import type {
	CurrentProfileResult,
	CurrentUserResult,
	GetAuthenticatorSettingsResult,
	UserDocumentTypesResult,
	UserDocumentValidationResult,
	UserProfileModel,
	ValidateDocumentResult
} from "../types/UserModel";
import {
	currentProfileResult,
	currentUserResult,
	getAuthenticatorSettingsResult,
	userDocumentTypesResult,
	userDocumentValidationResult,
	validateDocumentResult
} from "../types/UserModel";
import type { ListUserTournamentsResult } from "../types/UserTournamentModel";
import { listUserTournamentsResult } from "../types/UserTournamentModel";
import type { ListUserTransactionsResult } from "../types/UserTransactionModel";
import { listUserTransactionsResult } from "../types/UserTransactionModel";

const useUserService = () => {
	const actions = useHttpActions();

	return useMemo(
		() => ({
			getProfile: async () => {
				return BaseConverter.convert<CurrentProfileResult>(
					await actions.get(API_URLS.USER.PROFILE_INFO, null, {
						resolvers: [
							{
								resolver: currentProfileResult
							}
						]
					})
				);
			},
			updateProfile: async (data: Partial<UserProfileModel>) => {
				return BaseConverter.convert<CurrentProfileResult>(await actions.patch(API_URLS.USER.PROFILE_INFO, data));
			},
			getUserInfo: async () => {
				return BaseConverter.convert<CurrentUserResult>(
					await actions.get(API_URLS.USER.USER_INFO, null, {
						resolvers: [
							{
								resolver: currentUserResult
							}
						]
					})
				);
			},
			listTransactions: async (params: { startDate?: string; endDate?: string; status?: number }) => {
				return BaseConverter.convert<ListUserTransactionsResult>(
					await actions.get(API_URLS.TRANSACTIONS.LIST, params, {
						resolvers: [
							{
								resolver: listUserTransactionsResult
							}
						]
					})
				);
			},
			listTournaments: async (params: { dateFrom?: string; dateTo?: string }) => {
				return BaseConverter.convert<ListUserTournamentsResult>(
					await actions.get(API_URLS.TOURNAMENTS.HISTORY, params, {
						resolvers: [
							{
								resolver: listUserTournamentsResult
							}
						]
					})
				);
			},
			getDocumentTypes: async () => {
				return BaseConverter.convert<UserDocumentTypesResult>(
					await actions.get(API_URLS.DOCS.TYPES, null, {
						resolvers: [
							{
								resolver: userDocumentTypesResult
							}
						]
					})
				);
			},
			validateDocument: async (params: { file: any; documentType: string }) => {
				const formData = new FormData();
				formData.append("file1", params.file);
				formData.append("Description", params.documentType);
				formData.append("DocType", params.documentType);

				return BaseConverter.convert<ValidateDocumentResult>(
					await actions.post(API_URLS.DOCS.VALIDATE, formData, {
						headers: {
							"Content-Type": "multipart/form-data"
						},
						resolvers: [
							{
								resolver: validateDocumentResult
							}
						]
					})
				);
			},
			checkDocumentValidations: async () => {
				return BaseConverter.convert<UserDocumentValidationResult>(
					await actions.get(API_URLS.DOCS.VALIDATE, null, {
						resolvers: [
							{
								resolver: userDocumentValidationResult
							}
						]
					})
				);
			},
			updatePassword: async ({ password, newPassword }: { password: string; newPassword: string }) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.patch(API_URLS.USER.UPDATE_PASSWORD, {
						password,
						newPassword
					})
				);
			},
			getAuthenticatorSettings: async () => {
				return BaseConverter.convert<GetAuthenticatorSettingsResult>(
					await actions.put(API_URLS.USER.GET_AUTHENTICATOR_SETTINGS, null, {
						resolvers: [
							{
								resolver: getAuthenticatorSettingsResult
							}
						]
					})
				);
			},
			validateAuthenticatorCode: async (code: string) => {
				return BaseConverter.convert<BaseApiResponse>(
					await actions.post(
						API_URLS.USER.VALIDATE_AUTHENTICATOR_CODE,
						{
							code2FA: code
						},
						{
							resolvers: [
								{
									resolver: baseApiResponse
								}
							]
						}
					)
				);
			}
		}),
		[actions]
	);
};

export default useUserService;
